import { AppBar, Menu, Tab } from "@mui/material";
import { StyledComponentProps, withStyles } from "@mui/styles";
import { FeedClassKey, FeedStyleClasses, TabLargeStyle, TimelineClassKey, TimelineStyleClasses } from "assets/jss/common-styles";
import { AppBarTabs } from "components/AppTabs";
import ButtonDropdown from "components/Buttons/ButtonDropdown";
import ButtonResetFilter from "components/Buttons/ButtonResetFilter";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import { MuiFilterLine, MuiFilterToolbar } from "components/MuiExtras/Filter";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiMenuItemChecked from "components/MuiExtras/MuiMenuItemChecked";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import { FeedType, PublicationType } from "models/ApiEnums";
import { FeedAttachType, FeedFollowingPeriod, FeedHighlightsFilter } from "models/ApiModels";
import * as React from "react";
import { useTranslation, WithTranslation, withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";
import * as FeedActions from 'store/feed/actions';
import { FeedState } from "store/feed/reducer";
import { ApplicationState } from "store/rootReducer";
import CardManager from "views/Timeline/Components/CardManager";
import { ITimelineDispatchProps } from "views/Timeline/TimelineCommons";
import * as SideBarActions from 'store/sidebar/actions';


/////////////////////////////////////////////////////////////////////////////////////////////////

interface IStateProps {
    feed: FeedState;
}

interface IState {
    dialog: any;
    publicationTypeMenuAnchorEl: any;
    contentTypeMenuAnchorEl: any;
    periodMenuAnchorEl: any;
}

interface IProps {
    timestamp?: Date;
}

interface IDispatchProps extends ITimelineDispatchProps {
    onGetFeed: (filter: FeedHighlightsFilter, startIndex: number, endIndex: number) => Promise<any>;
}

type Props = IProps & IStateProps & IDispatchProps & StyledComponentProps<TimelineClassKey | FeedClassKey> & WithSnackbar & WithTranslation;

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IRenderMenu {
    category: FeedType;
    onChange: (event: React.ChangeEvent<{}>, value: any) => void;
}

const RenderMenuBar: React.FC<Props & IRenderMenu> = (props) => {
    const { t } = useTranslation();
    return (
        <div className={props.classes.filterTabContainer}>
            <AppBar position="static" color="default" classes={{ root: props.classes.appTabFilterRoot }}>
                <AppBarTabs
                    centered={true}
                    value={props.category}
                    onChange={props.onChange}
                >
                    <Tab value={FeedType.HighlightsLatestNews} label={t("Views.Timeline.TimelineHighlights.TabLatestNews")} style={TabLargeStyle} disabled={props.feed.loading} />
                    <Tab value={FeedType.HighlightsNews} label={t("Views.Timeline.TimelineHighlights.TabHighlights")} style={TabLargeStyle} disabled={props.feed.loading} />
                    <Tab value={FeedType.HighlightsOnHigh} label={t("Views.Timeline.TimelineHighlights.TabOnHigh")} style={TabLargeStyle} disabled={props.feed.loading} />
                </AppBarTabs>
            </AppBar>
        </div>
    );
};

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

class TimelineHighlightsComponent extends React.PureComponent<Props, IState> {

    private filter: FeedHighlightsFilter;

    constructor(props: Props) {
        super(props);
        this.state = {
            publicationTypeMenuAnchorEl: null,
            contentTypeMenuAnchorEl: null,
            periodMenuAnchorEl: null,
            dialog: null
        };

        this.filter = {
            category: FeedType.HighlightsLatestNews,
            searchText: null
        };
    }

    ///
    public feedReset() {
        this.props.onFeedReset();
    }

    ///
    private handleTabSelection = (event: React.ChangeEvent<{}>, value: any) => {
        if (this.props.feed.loading && this.filter.category == value) {
            return;
        }
        this.filter.category = value;
        this.feedReset();
    };


    //#region LATESTNEWS FILTER

    ///
    private get publicationTypeOptions() {
        return [
            { text: this.props.t("Common.Filter.NoFilter"), value: null },
            { text: this.props.t("Common.Filter.Toolbar.PublicationType.Post"), value: PublicationType.Post },
            { text: this.props.t("Common.Filter.Toolbar.PublicationType.Question"), value: PublicationType.Question },
            { text: this.props.t("Common.Filter.Toolbar.PublicationType.List"), value: PublicationType.List },
            { text: this.props.t("Common.Filter.Toolbar.PublicationType.Poll"), value: PublicationType.Poll }
        ];
    }

    ///
    private get contentTypeOptions() {
        return [
            { text: this.props.t("Common.Filter.NoFilter"), value: null },
            { text: this.props.t("Common.Filter.Toolbar.Content.Video"), value: FeedAttachType.Video },
            { text: this.props.t("Common.Filter.Toolbar.Content.Image"), value: FeedAttachType.Image },
            { text: this.props.t("Common.Filter.Toolbar.Content.File"), value: FeedAttachType.File }
        ];
    }

    ///
    private get periodOptions() {
        return [
            { text: this.props.t("Common.Filter.NoFilter"), value: null },
            { text: this.props.t("Common.Filter.Toolbar.Period.Today"), value: FeedFollowingPeriod.Today },
            { text: this.props.t("Common.Filter.Toolbar.Period.LastWeek"), value: FeedFollowingPeriod.LastWeek },
            { text: this.props.t("Common.Filter.Toolbar.Period.LastMonth"), value: FeedFollowingPeriod.LastMonth },
            { text: this.props.t("Common.Filter.Toolbar.Period.LastQuarter"), value: FeedFollowingPeriod.LastQuarter },
            { text: this.props.t("Common.Filter.Toolbar.Period.LastYear"), value: FeedFollowingPeriod.LastYear }
        ];
    }

    ///
    private isLatestNewsFiltered() {
        return this.filter.attachType != null || this.filter.period != null || this.filter.publicationType != null;
    }

    ///
    private renderLatestNewsAdvFilter(): JSX.Element {
        return (
            <MuiContainer>
                <AppBar color="secondary" position="static">
                    <MuiFilterToolbar variant="dense" >
                        <MuiFilterLine>
                            <ButtonDropdown
                                text={this.filter.publicationType == null ? this.props.t("Common.Filter.Toolbar.PublicationType.Any") : this.publicationTypeOptions.find(p => p.value == this.filter.publicationType).text}
                                onClick={(e) => { this.setState({ publicationTypeMenuAnchorEl: e.currentTarget }); }
                                }
                            />
                            <Menu
                                anchorEl={this.state.publicationTypeMenuAnchorEl}
                                open={this.state.publicationTypeMenuAnchorEl != null}
                                onClose={() => { this.setState({ publicationTypeMenuAnchorEl: null }); }}
                            >
                                {
                                    this.publicationTypeOptions.map((v, index) =>
                                        <MuiMenuItemChecked key={index} text={v.text} value={v.value} currentSelection={this.filter.publicationType} onClick={this.applyPublicationType} />
                                    )
                                }
                            </Menu>

                            <ButtonDropdown
                                text={this.filter.attachType == null ? this.props.t("Common.Filter.Toolbar.Content.Any") : this.contentTypeOptions.find(p => p.value == this.filter.attachType).text}
                                onClick={(e) => { this.setState({ contentTypeMenuAnchorEl: e.currentTarget }); }
                                }
                            />
                            <Menu
                                anchorEl={this.state.contentTypeMenuAnchorEl}
                                open={this.state.contentTypeMenuAnchorEl != null}
                                onClose={() => { this.setState({ contentTypeMenuAnchorEl: null }); }}
                            >
                                {
                                    this.contentTypeOptions.map((v, index) =>
                                        <MuiMenuItemChecked key={index} text={v.text} value={v.value} currentSelection={this.filter.attachType} onClick={this.applyContent} />
                                    )
                                }
                            </Menu>

                            <ButtonDropdown
                                text={this.filter.period == null ? this.props.t("Common.Filter.Toolbar.Period.Any") : this.periodOptions.find(p => p.value == this.filter.period).text}
                                onClick={(e) => { this.setState({ periodMenuAnchorEl: e.currentTarget }); }
                                }
                            />
                            <Menu
                                anchorEl={this.state.periodMenuAnchorEl}
                                open={this.state.periodMenuAnchorEl != null}
                                onClose={() => { this.setState({ periodMenuAnchorEl: null }); }}
                            >
                                {
                                    this.periodOptions.map((v, index) =>
                                        <MuiMenuItemChecked key={index} text={v.text} value={v.value} currentSelection={this.filter.period} onClick={this.applyPeriod} />
                                    )
                                }
                            </Menu>

                        </MuiFilterLine>
                    </MuiFilterToolbar>
                </AppBar>
            </MuiContainer>
        );
    }

    ///
    private applyContent = (attachType: FeedAttachType) => {
        this.filter.attachType = attachType;
        this.setState({ contentTypeMenuAnchorEl: null });
        this.props.onFeedReset();
    };

    ///
    private applyPublicationType = (publicationType: PublicationType) => {
        this.filter.publicationType = publicationType;
        this.setState({ publicationTypeMenuAnchorEl: null });
        this.props.onFeedReset();
    };

    ///
    private applyPeriod = (period: FeedFollowingPeriod) => {
        this.filter.period = period;
        this.setState({ periodMenuAnchorEl: null });
        this.props.onFeedReset();
    };

    ///
    private resetFilter = () => {
        this.filter.period = null;
        this.filter.attachType = null;
        this.filter.publicationType = null;
        this.props.onFeedReset();
    };

    //#endregion

    ///
    public componentDidMount() {
        this.feedReset();
    }

    ///
    public componentWillUnmount() {
        this.feedReset();
    }

    ///
    public componentDidUpdate(prevProps: Props) {
        if (prevProps.timestamp != this.props.timestamp && !this.props.feed.loading)
            this.feedReset();
    }

    ///
    public render() {
        const { classes, feed, feed: { lastError, loading, totalRecords }, snackbar } = this.props;

        return (
            <div>
                <RenderMenuBar {...this.props} category={this.filter.category} onChange={this.handleTabSelection} />

                <div className={classes.timelineContainer}>

                    {this.state.dialog}

                    {
                        this.filter.category == FeedType.HighlightsLatestNews
                        && (feed?.data?.length > 0 || this.isLatestNewsFiltered() || loading)
                        && this.renderLatestNewsAdvFilter()
                    }

                    <CardManager
                        classes={classes}
                        feedType={this.filter.category}
                        feed={feed}
                        snackbar={snackbar}
                        onExpandPublication={this.props.onExpandPublication}
                        onReloadPublication={this.props.onReloadPublication}
                        onRemovePublication={this.props.onRemovePublication}
                        onChangePublicationVisibility={this.props.onChangePublicationVisibility}
                        onUpdatePublication={this.props.onUpdateFeedData}
                        onSidebarResetRequest={this.props.onSidebarResetRequest}
                        onRemoveFromFeed={this.props.onRemoveFromFeed}
                        onGetFeed={(startIndex: number, endIndex: number) => { return this.props.onGetFeed(this.filter, startIndex, endIndex); }}
                        onFeedReset={() => this.props.onFeedReset()}
                    />

                    {
                        lastError != null
                            ? <RequestErrorReport error={lastError} />
                            : this.props.feed.data.length == 0 && !loading && !this.isLatestNewsFiltered()
                                ? <MuiMessageBox variant="info" message={this.props.t("Common.Feed.Filter.PublicationResultText_zero")} />
                                : this.props.feed.data.length == 0 && !loading && this.isLatestNewsFiltered()
                                    ? <MuiMessageBox
                                        variant="warning"
                                        action={<ButtonResetFilter color="warning" onClick={this.resetFilter} />}
                                        message={this.props.t("Common.Feed.Filter.PublicationFilteredResultText_zero")}
                                    />
                                    : null
                    }

                </div>
            </div>
        );
    }
}

/////////////////////////////////////////////////////////////////////////////////////////////////

function mapStateToProps(state: ApplicationState): IStateProps {
    return {
        feed: state.feed
    };
}

function mapDispatchToProps(dispatch: ThunkDispatch<ApplicationState, void, Action<any>>, ownProps: IProps): IDispatchProps {
    const action = new FeedActions.HighlightsFeedAction();

    return {
        onReloadPublication: (uid, publicationId) => dispatch(action.reloadPublication(uid, publicationId)),
        onExpandPublication: (uid, publicationId) => dispatch(action.getPublicationFullContent(uid, publicationId)),
        onChangePublicationVisibility: (uid, publicationId, visibility) => dispatch(action.changePublicationVisibility(uid, publicationId, visibility)),
        onRemovePublication: (uid, publicationId, publicationType) => dispatch(action.removePublication(uid, publicationId, publicationType)),
        onUpdateFeedData: (newItemDto) => dispatch(action.updateFeedData(newItemDto)),
        onRemoveFromFeed: (uid, publicationId) => dispatch(action.removeFeedData(uid, publicationId)),
        onFeedReset: () => dispatch(action.feedReset()),
        onGetFeed: (filter, startIndex, endIndex) => dispatch(action.getFeed(filter, startIndex, endIndex)),

        onSidebarResetRequest: () => dispatch(SideBarActions.reset()),
    };
}


const withAllStyles = withStyles(
    (theme) => ({
        ...TimelineStyleClasses(theme),
        ...FeedStyleClasses(theme),
    }),
    { withTheme: true },
);

const Timeline = connect<IStateProps, IDispatchProps>(mapStateToProps, mapDispatchToProps)(withSnackbar(withTranslation()(withAllStyles(TimelineHighlightsComponent))));

///////////////////////////////////////////////////////////////////////////

const TimelineHighlights: React.FC<Props> = () => {
    return (
        <Timeline
            timestamp={new Date()}
        />
    );
};

export default TimelineHighlights;